import { useEffect, useState } from "react";
import { http, rp, rpShort, nf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Stat } from "@/components/Ui";
import AiInsight from "@/components/AiInsight";
import ExportButtons from "@/components/ExportButtons";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const BRANCHES = ["Surabaya", "Jakarta", "Medan", "Semarang", "Bandung", "Palembang", "Pekanbaru", "Makassar", "Banjarmasin", "Balikpapan"];

export default function ReportSales() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [bulan, setBulan] = useState("");
  const [tahun, setTahun] = useState("");

  useEffect(() => {
    const params = {};
    if (bulan) params.bulan = bulan;
    if (tahun) params.tahun = tahun;
    http.get("/reports/sales", { params }).then((r) => setD(r.data)).catch(() => setD({ rows: [], per_cabang: {} }));
  }, [bulan, tahun]);

  if (!d) return <div className="p-8 mono text-sm">Memuat laporan…</div>;
  const cols = user.role === "cabang" ? [user.branch] : BRANCHES;
  const chart = Object.entries(d.per_cabang || {}).map(([cabang, qty]) => ({ cabang, qty }));
  const sel = "h-10 px-3 border border-neutral-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#4338CA]";

  return (
    <div data-testid="report-sales-page">
      <PageHeader subtitle="Laporan" title="Laporan Penjualan Cabang & Nasional" testId="report-sales-header">
        <select data-testid="report-sales-tahun-filter" className={sel} value={tahun} onChange={(e) => setTahun(e.target.value)}>
          <option value="">Semua Tahun</option>
          {(d.tahun_list || []).map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select data-testid="report-sales-bulan-filter" className={sel} value={bulan} onChange={(e) => setBulan(e.target.value)}>
          <option value="">Semua Bulan</option>
          {(d.bulan_list || []).map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <ExportButtons kind="sales" params={{ ...(tahun ? { tahun } : {}), ...(bulan ? { bulan } : {}) }} testId="export-sales" />
      </PageHeader>

      <div className="p-6 sm:p-8 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Stat testId="report-sales-total-qty" label="Total Qty Penjualan" value={nf(d.total_qty)} />
          <Stat testId="report-sales-total-nominal" label="Total Nominal Penjualan" value={rpShort(d.total_nominal)} sub={rp(d.total_nominal)} accent="#16A34A" />
          <Stat testId="report-sales-count" label="Produk Terjual" value={nf(d.rows.length)} />
        </div>

        <div className="erp-panel p-4">
          <div className="mono text-[11px] uppercase tracking-widest text-[#525252] mb-2">Rekap Penjualan per Cabang</div>
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart}>
                <CartesianGrid stroke="#eee" vertical={false} />
                <XAxis dataKey="cabang" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={60} interval={0} />
                <YAxis tick={{ fontSize: 10 }} width={55} />
                <Tooltip formatter={(v) => nf(v)} />
                <Bar dataKey="qty" fill="#16A34A" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="erp-panel overflow-auto max-h-[70vh]" data-testid="report-sales-table-wrap">
          <table className="erp-table">
            <thead>
              <tr>
                <th>Kode Stock</th><th>Deskripsi Produk</th><th>Type</th><th>Spesifikasi</th>
                {cols.map((b) => <th key={b} className="num">{b}</th>)}
                <th className="num">Total Qty</th><th className="num">Harga Jual A</th><th className="num">Nominal</th>
              </tr>
            </thead>
            <tbody>
              {d.rows.map((r, i) => (
                <tr key={i} data-testid={`report-sales-row-${r.kode_stock}`}>
                  <td className="mono font-medium">{r.kode_stock}</td>
                  <td className="max-w-[200px] truncate">{r.deskripsi_produk}</td>
                  <td>{r.type_produk}</td>
                  <td className="max-w-[200px] truncate">{r.spesifikasi_produk}</td>
                  {cols.map((b) => <td key={b} className="num">{nf(r.cabang?.[b] || 0)}</td>)}
                  <td className="num font-semibold">{nf(r.qty)}</td>
                  <td className="num">{rp(r.harga_jual_a)}</td>
                  <td className="num font-semibold">{rp(r.nominal)}</td>
                </tr>
              ))}
              {d.rows.length === 0 && (
                <tr><td colSpan={18} className="text-center text-neutral-500 py-6">Belum ada data penjualan</td></tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4}>TOTAL</td>
                {cols.map((b) => <td key={b} className="num">{nf(d.per_cabang?.[b] || 0)}</td>)}
                <td className="num">{nf(d.total_qty)}</td>
                <td className="num">—</td>
                <td className="num">{rp(d.total_nominal)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <AiInsight scope="sales" />
      </div>
    </div>
  );
}
